<template>
  <div class="selector-pagos-combinados">
    <div v-for="(pago, index) in pagos" :key="index" class="row g-2 mb-2 align-items-center">
      <div class="col-md-3">
        <select v-model="pago.IdMetodoPago" class="form-select" @change="onMetodoChange(index)">
          <option :value="null" disabled>Seleccione método</option>
          <option v-for="m in metodosDisponibles(index)" :key="m.IdMetodoPago" :value="m.IdMetodoPago">
            {{ m.Metodo }}
          </option>
        </select>
      </div>
      <div class="col-md-2">
        <input
          type="number"
          v-model.number="pago.Monto"
          min="0.01"
          step="0.01"
          class="form-control"
          placeholder="Monto Q"
          :disabled="!pago.IdMetodoPago"
        />
      </div>

      <!-- Cuenta bancaria (solo depósito/transferencia = 2) -->
      <div class="col-md-3" v-if="pago.IdMetodoPago === 2">
        <select v-model="pago.IdCuentaBancaria" class="form-select">
          <option :value="null" disabled>Cuenta bancaria</option>
          <option v-for="c in cuentasBancarias" :key="c.IdCuentaBancaria" :value="c.IdCuentaBancaria">
            {{ c.Banco }} - {{ c.NumeroCuenta }}
          </option>
        </select>
      </div>

      <!-- Referencia (métodos 2, 3, 4) -->
      <div class="col-md-3" v-if="[2, 3, 4].includes(pago.IdMetodoPago)">
        <input type="text" v-model="pago.NumeroReferencia" class="form-control" placeholder="Referencia" />
      </div>

      <div class="col-md-1">
        <button
          type="button"
          class="btn btn-outline-danger btn-sm"
          @click="quitarPago(index)"
          :disabled="pagos.length <= 2"
          title="Quitar método"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <button
      type="button"
      class="btn btn-outline-success btn-sm mb-3"
      @click="agregarPago"
      :disabled="pagos.length >= 5"
    >
      <i class="bi bi-plus-lg me-1"></i>Agregar método de pago
    </button>

    <div class="alert py-2 mb-0" :class="claseResumen">
      <div class="d-flex justify-content-between">
        <span>Total a cubrir:</span>
        <strong>Q{{ total.toFixed(2) }}</strong>
      </div>
      <div class="d-flex justify-content-between">
        <span>Suma capturada:</span>
        <strong>Q{{ sumaCapturada.toFixed(2) }}</strong>
      </div>
      <div v-if="diferencia > 0.009" class="d-flex justify-content-between border-top pt-1 mt-1">
        <span>Faltan:</span>
        <strong>Q{{ diferencia.toFixed(2) }}</strong>
      </div>
      <div v-else-if="vuelto > 0.009" class="d-flex justify-content-between border-top pt-1 mt-1">
        <span>Vuelto (se descuenta del efectivo):</span>
        <strong>Q{{ vuelto.toFixed(2) }}</strong>
      </div>
    </div>
    <div v-if="mensajeError" class="small text-danger mt-2">
      <i class="bi bi-exclamation-triangle me-1"></i>{{ mensajeError }}
    </div>
  </div>
</template>

<script>
/**
 * Repetidor de métodos de pago para el flujo de Pago Combinado (2 a 5
 * métodos distintos sobre un mismo total). Estructura visual basada en el
 * repetidor de ventaserviciosextra.vue. Usado SOLO por los modales nuevos
 * de pago combinado (hotel y restaurante) — no toca los modales de pago
 * de un solo método.
 */
export default {
  name: 'SelectorPagosCombinados',
  props: {
    total: {
      type: Number,
      required: true,
    },
    metodosPago: {
      type: Array,
      required: true,
    },
    cuentasBancarias: {
      type: Array,
      default: () => [],
    },
    modelValue: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['update:modelValue', 'update:valido'],
  data() {
    return {
      pagos: this.modelValue && this.modelValue.length >= 2
        ? this.modelValue.map((p) => ({ ...p }))
        : [
          { IdMetodoPago: null, Monto: null, IdCuentaBancaria: null, NumeroReferencia: '' },
          { IdMetodoPago: null, Monto: null, IdCuentaBancaria: null, NumeroReferencia: '' },
        ],
    };
  },
  computed: {
    sumaCapturada() {
      return Math.round(this.pagos.reduce((acc, p) => acc + (Number(p.Monto) || 0), 0) * 100) / 100;
    },
    diferencia() {
      return Math.max(0, Math.round((this.total - this.sumaCapturada) * 100) / 100);
    },
    vuelto() {
      return Math.max(0, Math.round((this.sumaCapturada - this.total) * 100) / 100);
    },
    tieneEfectivo() {
      return this.pagos.some((p) => Number(p.IdMetodoPago) === 1);
    },
    metodosUsados() {
      return this.pagos.map((p) => p.IdMetodoPago).filter((v) => v !== null && v !== '');
    },
    pagosConMonto() {
      return this.pagos.filter((p) => p.IdMetodoPago && Number(p.Monto) > 0);
    },
    mensajeError() {
      const metodos = this.metodosUsados;
      if (metodos.length !== new Set(metodos).size) {
        return 'No puedes repetir el mismo método de pago.';
      }
      if (this.vuelto > 0.009 && !this.tieneEfectivo) {
        return 'El vuelto solo puede descontarse del efectivo. Agrega un renglón en efectivo o ajusta los montos.';
      }
      if (this.pagosConMonto.length < 2) {
        return 'Captura al menos dos métodos con monto.';
      }
      for (const p of this.pagosConMonto) {
        if ([2, 3, 4].includes(Number(p.IdMetodoPago)) && !String(p.NumeroReferencia || '').trim()) {
          return 'Falta el número de referencia en uno o más pagos.';
        }
        if (Number(p.IdMetodoPago) === 2 && !p.IdCuentaBancaria) {
          return 'Falta la cuenta bancaria en el pago de depósito/transferencia.';
        }
      }
      return '';
    },
    esValido() {
      return !this.mensajeError && this.diferencia <= 0.009;
    },
    claseResumen() {
      if (this.diferencia > 0.009) return 'alert-danger';
      if (this.vuelto > 0.009) return 'alert-warning';
      return 'alert-success';
    },
  },
  watch: {
    pagos: {
      deep: true,
      handler(val) {
        this.$emit('update:modelValue', val.map((p) => ({ ...p })));
      },
    },
    esValido: {
      immediate: true,
      handler(val) {
        this.$emit('update:valido', val);
      },
    },
  },
  methods: {
    metodosDisponibles(index) {
      const actual = this.pagos[index].IdMetodoPago;
      const usadosPorOtros = this.metodosUsados.filter((m) => m !== actual);
      return this.metodosPago.filter((m) => !usadosPorOtros.includes(m.IdMetodoPago));
    },
    onMetodoChange(index) {
      this.pagos[index].IdCuentaBancaria = null;
      this.pagos[index].NumeroReferencia = '';
    },
    agregarPago() {
      if (this.pagos.length >= 5) return;
      this.pagos.push({ IdMetodoPago: null, Monto: null, IdCuentaBancaria: null, NumeroReferencia: '' });
    },
    quitarPago(index) {
      if (this.pagos.length <= 2) return;
      this.pagos.splice(index, 1);
    },
  },
};
</script>
